import { ErrorResponse } from 'react-router-dom';
import {
  IOrganization,
  IPermissionTree,
  IProfileSettingsInitialValues,
  IResetPasswordBody,
  IResponse,
  IRole,
  IRolePermissions,
  ISignInBody,
  ISignInResponse,
  IWithViewPerm,
} from '../types';
import { RequestMethods } from '../services/enum';
import { IUseHttpServiceProps } from '../services/type';
import { preparePermissionsPayload } from '../utils/functions';

export default class AuthService {
  private readonly baseUrl = 'auth/';

  constructor(
    private readonly executeService: <T>(
      args: IUseHttpServiceProps,
    ) => Promise<T | ErrorResponse | null>,
  ) {}

  async login(
    credentials: ISignInBody,
  ): Promise<ISignInResponse | ErrorResponse | null> {
    return await this.executeService<ISignInResponse>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}token/`,
      data: credentials,
    });
  }

  async resetPassword(email: string): Promise<unknown | ErrorResponse | null> {
    return await this.executeService<unknown>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}password-reset/send/`,
      data: { email },
    });
  }

  async confirmResetPassword(
    values: IResetPasswordBody,
  ): Promise<{ message: string } | ErrorResponse | null> {
    return await this.executeService<{ message: string }>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}password-reset/confirm/`,
      data: values,
    });
  }

  async fetchWithViewPerm(
    model: string,
    organizationId: number,
  ): Promise<IResponse<IWithViewPerm> | ErrorResponse | null> {
    const query = new URLSearchParams({
      model,
      organization: organizationId.toString(),
    }).toString();

    return await this.executeService<IResponse<IWithViewPerm>>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}user/with-view-perm/?${query}`,
    });
  }

  async fetchRoles(): Promise<IRole[] | ErrorResponse | null> {
    return await this.executeService<IRole[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}role/`,
    });
  }

  async fetchPermissionTree(): Promise<IPermissionTree | ErrorResponse | null> {
    return await this.executeService<IPermissionTree>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}permission/`,
    });
  }

  async fetchOrganizations(): Promise<
    ErrorResponse | IResponse<IOrganization> | null
  > {
    return await this.executeService<IResponse<IOrganization>>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}organization/?ordering=name`,
    });
  }

  async fetchOrganizationRoles(
    id: number,
  ): Promise<IRole[] | ErrorResponse | null> {
    return await this.executeService<IRole[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}organization/${id}/roles/`,
    });
  }

  async fetchRolePermissions(
    id: number | string,
  ): Promise<IRolePermissions | ErrorResponse | null> {
    return await this.executeService<IRolePermissions>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}role/${id}/`,
    });
  }

  async assignPermissions(
    id: number,
    values: IProfileSettingsInitialValues,
  ): Promise<unknown | ErrorResponse | null> {
    const data = preparePermissionsPayload(values);
    return await this.executeService<unknown>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}role/${id}/assign-permissions/`,
      data,
    });
  }

  async createRole(
    values: IProfileSettingsInitialValues,
  ): Promise<unknown | ErrorResponse | null> {
    const data = preparePermissionsPayload(values);
    return await this.executeService<unknown>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}role/`,
      data,
    });
  }

  async deleteRole(id: number): Promise<unknown | ErrorResponse | null> {
    return await this.executeService<unknown>({
      method: RequestMethods.DELETE,
      url: `${this.baseUrl}role/${id}/`,
    });
  }
}
